import React, { useEffect, useState } from "react";
import { useForm, useSelect } from "@refinedev/antd";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Tooltip,
  Badge,
  Space,
  Switch,
  DatePicker,
  Alert
} from "antd";
import type { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { MinusCircleOutlined, PlusOutlined, FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

interface TaxOption {
  label: string;
  value: number;
  data: {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
  };
}

const formatCurrency = (value: number | undefined): string => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const generateOrderId = (): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let orderId = '';
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    orderId += characters[randomIndex];
  }
  return orderId;
};

export const PurchaseOrderCreate: React.FC = () => {
  const [isManualVendor, setIsManualVendor] = useState(false);
  const [standardTaxRate, setStandardTaxRate] = useState(15.5);
  const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);

  const { form, formProps, saveButtonProps } = useForm({
    resource: "purchaseorders",
    meta: {
      fields: [
        "id",
        "orderNumber",
        "vendorId",
        "currencyTypeCode",
        "exchangeRate",
        "subTotal",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "status",
        "orderDate",
        "deliveryDate",
        "isManualVendor",
        "supplierCode",
        "supplierTPIN",
        "manualVendorName",
        "manualVendorContact",
        "manualVendorPhone",
        "manualVendorEmail",
        {
          items: [
            "purchaseOrderId",
            "inventoryItemId",
            "itemCode",
            "itemName",
            "itemClassCode",
            "quantity",
            "taxRate",
            "unitPrice",
            "discountRate",
            "discountAmount",
            "totalAmount",
            "taxAmount",
            "lineAmount",
            "isActive",
            "taxType"
          ]
        }
      ]
    }
  });

  const { selectProps: vendorSelectProps, queryResult: vendorQueryResult } = useSelect({
    resource: "vendors",
    optionLabel: "name",
    optionValue: "id",
    meta: {
      fields: ["id", "name", "tpin", "description", "email", "phone", "address"]
    },
    onSearch: (value: string) => [
      {
        field: "name",
        operator: "contains",
        value,
      },
    ],
  });

  const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
    resource: "inventory",
    optionLabel: "itemName",
    optionValue: "itemCode",
    meta: {
      fields: [
        "id",
        "itemCode",
        "itemName",
        "itemClassificationCode",
        "quantityUnitCode",
        "barcode",
        "additionalInformation",
        "isActive",
        {
          stockLevel: [
            "id",
            "currentStockLevel",
            "availableStock"
          ]
        },
        {
          prices: [
            "id",
            "basePrice",
            "effectivePrice",
            "isActive",
            "currencyCode",
            "currencyType"
          ]
        }
      ]
    }
  });

  const { data: currencies, isLoading: currenciesLoading } = useQuery({
    queryKey: ["currencies"],
    queryFn: () => api.getCurrencies(),
    onError: (error) => {
      console.error("Error fetching currencies:", error);
    }
  });

  const { data: taxData } = useQuery({
    queryKey: ["taxes"],
    queryFn: async () => {
      const response = await api.get("/taxes");
      return response.data;
    },
  });

  useEffect(() => {
    if (taxData) {
      const options = taxData.map((tax: any) => ({
        label: `${tax.taxType} (${tax.rate}%)`,
        value: tax.rate,
        data: tax,
      }));
      setTaxOptions(options);
    }
  }, [taxData]);

  useEffect(() => {
    if (currencies && currencies.length > 0) {
      const defaultCurrency = currencies.find((c: any) => c.currencyCode === 'USD') || currencies[0];
      if (defaultCurrency && !form.getFieldValue('currencyTypeCode')) {
        form.setFieldsValue({
          currencyTypeCode: defaultCurrency.currencyCode,
          exchangeRate: defaultCurrency.exchangeRate || 1.0
        });
      }
    }
  }, [currencies, form]);

  const handleVendorSelect = async (value: string) => {
    const vendorData = vendorQueryResult?.data?.data?.find(
      (vendor: any) => vendor.id.toString() === value.toString()
    );

    if (vendorData) {
      form.setFieldsValue({
        supplierCode: vendorData.id.toString(),
        supplierTPIN: vendorData.tpin || '',
        vendorName: vendorData.name
      });
    }
  };

  const handleManualVendorToggle = (checked: boolean) => {
    setIsManualVendor(checked);

    if (checked) {
      form.setFieldsValue({
        vendorId: undefined,
        supplierCode: '',
        supplierTPIN: '',
        vendorName: '',
        isManualVendor: true
      });
    } else {
      form.setFieldsValue({
        manualVendorName: '',
        manualVendorContact: '',
        manualVendorPhone: '',
        manualVendorEmail: '',
        isManualVendor: false
      });
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      orderNumber: `PO-${generateOrderId()}`,
      currencyTypeCode: "USD",
      exchangeRate: 1.0,
      orderDate: dayjs(),
      status: "Draft",
      subTotal: 0,
      totalTaxableAmount: 0,
      totalTax: 0,
      totalAmount: 0,
      isManualVendor: false,
      items: []
    });
  }, [form]);

  const handleItemSelect = (itemCode: string, itemIndex: number) => {
    const item = itemQueryResult?.data?.data?.find((i: any) => i.itemCode === itemCode);
    if (item) {
      const currentCurrency = form.getFieldValue('currencyTypeCode') || 'USD';
      const selectedCurrency = currencies?.find((c: any) => c.currencyCode === currentCurrency);
      
      const activePrice = item.prices?.find((price: any) => 
        price.isActive && 
        (price.currencyCode === currentCurrency || price.currencyType === currentCurrency)
      );

      form.setFieldValue(['items', itemIndex, 'itemName'], item.itemName);
      form.setFieldValue(['items', itemIndex, 'itemClassCode'], item.itemClassificationCode);
      form.setFieldValue(['items', itemIndex, 'quantityUnitCode'], item.quantityUnitCode);
      form.setFieldValue(['items', itemIndex, 'availableStock'], item.stockLevel?.availableStock || 0);
      form.setFieldValue(['items', itemIndex, 'inventoryItemId'], item.id);
      
      if (selectedCurrency) {
        form.setFieldValue(['items', itemIndex, 'currencyId'], selectedCurrency.id);
      }
      
      if (activePrice) {
        form.setFieldValue(['items', itemIndex, 'unitPrice'], activePrice.effectivePrice || activePrice.basePrice);
        form.setFieldValue(['items', itemIndex, 'priceId'], activePrice.id);
      }

      form.setFieldValue(['items', itemIndex, 'taxRate'], standardTaxRate);
      
      calculateItemTotals(itemIndex);
    }
  };

  const handleCurrencyChange = async (value: string) => {
    const selectedCurrency = currencies?.find(
      (currency: any) => currency.currencyCode === value
    );

    if (selectedCurrency) {
      form.setFieldsValue({
        exchangeRate: selectedCurrency.exchangeRate || 1.0
      });

      const currentItems = form.getFieldValue('items') || [];
      if (currentItems.length > 0) {
        const updatedItems = currentItems.map((item: any) => ({
          ...item,
          currencyId: selectedCurrency.id
        }));

        form.setFieldsValue({
          items: updatedItems
        });
      }
    } else {
      form.setFieldsValue({
        exchangeRate: 1.0
      });
    }
  };

  const calculateItemTotals = (itemIndex: number) => {
    const items = form.getFieldValue('items') || [];
    const item = items[itemIndex];
    
    if (item && item.quantity && item.unitPrice) {
      const lineAmount = item.quantity * item.unitPrice;
      const discountAmount = lineAmount * (item.discountRate || 0) / 100;
      const taxableAmount = lineAmount - discountAmount;
      const taxAmount = taxableAmount * (item.taxRate || 0) / 100;
      const totalAmount = taxableAmount + taxAmount;

      form.setFieldValue(['items', itemIndex, 'lineAmount'], lineAmount);
      form.setFieldValue(['items', itemIndex, 'discountAmount'], discountAmount);
      form.setFieldValue(['items', itemIndex, 'taxAmount'], taxAmount);
      form.setFieldValue(['items', itemIndex, 'totalAmount'], totalAmount);

      calculateOrderTotals();
    }
  };

  const calculateOrderTotals = () => {
    const items = form.getFieldValue('items') || [];
    
    let subTotal = 0;
    let totalTaxableAmount = 0;
    let totalTax = 0;
    let totalAmount = 0;

    items.forEach((item: any) => {
      if (item && item.quantity && item.unitPrice) {
        const lineAmount = item.quantity * item.unitPrice;
        const discountAmount = lineAmount * (item.discountRate || 0) / 100;
        const taxableAmount = lineAmount - discountAmount;
        const taxAmount = taxableAmount * (item.taxRate || 0) / 100;
        const itemTotal = taxableAmount + taxAmount;

        subTotal += lineAmount;
        totalTaxableAmount += taxableAmount;
        totalTax += taxAmount;
        totalAmount += itemTotal;
      }
    });

    form.setFieldsValue({
      subTotal,
      totalTaxableAmount,
      totalTax,
      totalAmount
    });
  };

  return (
    <div className="show-page-wrapper">
      <PageHeader
        title="Create Purchase Order"
        titleIcon={<FileTextOutlined />}
        breadcrumbs={[
          { title: "Purchase Orders", href: "/purchaseorders" },
          { title: "Create Purchase Order" },
        ]}
        showBack={true}
      />

      <Form {...formProps} layout="vertical" onFinish={(values) => {
        let supplierCode = values.supplierCode;
        let supplierTPIN = values.supplierTPIN;

        if (values.isManualVendor) {
          supplierCode = 'MANUAL-' + generateOrderId();
          supplierTPIN = values.supplierTPIN || 'NONE';
        }

        const transformedValues = {
          ...values,
          orderDate: values.orderDate ? dayjs(values.orderDate).toISOString() : dayjs().toISOString(),
          deliveryDate: values.deliveryDate ? dayjs(values.deliveryDate).toISOString() : null,
          supplierCode,
          supplierTPIN,
          ...(values.isManualVendor && {
            manualVendor: {
              name: values.manualVendorName,
              contact: values.manualVendorContact,
              phone: values.manualVendorPhone,
              email: values.manualVendorEmail,
            }
          }),
        };
        formProps.onFinish?.(transformedValues);
      }}>
        
        {/* Order Information Card */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Order Information</h3>
            <Badge
              count={form.getFieldValue('status') === "Confirmed" ? "CONFIRMED" : "DRAFT"}
              style={{
                backgroundColor: form.getFieldValue('status') === "Confirmed" ? "var(--color-success)" : "#bfbfbf",
                color: "#fff",
              }}
            />
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Purchase Order Number"
                  name="orderNumber"
                  rules={[{ required: true, message: 'Please input the order number!' }]}
                >
                  <Input placeholder="PO Number" style={{ fontWeight: 'bold' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Currency"
                  name="currencyTypeCode"
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder="Select currency"
                    onChange={handleCurrencyChange}
                    loading={currenciesLoading}
                  >
                    {currencies?.map((currency: any) => (
                      <Select.Option key={currency.id} value={currency.currencyCode}>
                        {currency.currencyCode} ({currency.symbol}) - {currency.currencyName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Exchange Rate"
                  name="exchangeRate"
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="1.00" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Vendor Information Card */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Vendor Information</h3>
          </div>
          <div className="info-card-body">
            {/* Manual Vendor Toggle */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24}>
                <Space align="center" size={12}>
                  <Switch
                    checked={isManualVendor}
                    onChange={handleManualVendorToggle}
                    checkedChildren="New Vendor"
                    unCheckedChildren="Select Existing"
                  />
                  <Text type="secondary">Create new vendor</Text>
                </Space>
              </Col>
            </Row>

            {!isManualVendor ? (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Vendor"
                    name="vendorId"
                    rules={[{ required: !isManualVendor, message: 'Please select a vendor!' }]}
                  >
                    <Select
                      {...vendorSelectProps}
                      showSearch
                      placeholder="Search and select vendor"
                      filterOption={false}
                      onChange={handleVendorSelect}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Vendor Name" name="vendorName">
                    <Input placeholder="Vendor name will be auto-filled" readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Supplier Code"
                    name="supplierCode"
                    rules={[{ required: !isManualVendor }]}
                  >
                    <Input placeholder="Auto-filled from vendor" readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Supplier TPIN"
                    name="supplierTPIN"
                    rules={[{ required: !isManualVendor }]}
                  >
                    <Input placeholder="Auto-filled from vendor" readOnly />
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
                  <Col span={24}>
                    <Alert
                      message="Create New Vendor"
                      description="Enter details for a new vendor. This vendor will be saved and available for future transactions."
                      type="info"
                      showIcon
                      banner
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Vendor Name"
                      name="manualVendorName"
                      rules={[{ required: isManualVendor }]}
                    >
                      <Input placeholder="Enter vendor name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Vendor TPIN" name="supplierTPIN">
                      <Input placeholder="Enter vendor TPIN (optional)" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Contact" name="manualVendorContact">
                      <Input placeholder="Enter contact person" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Phone" name="manualVendorPhone">
                      <Input placeholder="Enter phone number" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Email" name="manualVendorEmail">
                      <Input placeholder="Enter email address" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Form.Item name="isManualVendor" hidden>
              <Input />
            </Form.Item>
          </div>
        </div>

        {/* Purchase Order Items Card */}
        <Card title="Purchase Order Items" className="data-table-card" style={{ marginBottom: 16 }}>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    style={{ marginBottom: 12 }}
                    size="small"
                    type="inner"
                    extra={
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    }
                  >
                    <Row gutter={[12, 12]}>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item
                          {...restField}
                          label="Item"
                          name={[name, 'itemCode']}
                          rules={[{ required: true, message: 'Select item' }]}
                        >
                          <Select
                            {...itemSelectProps}
                            showSearch
                            placeholder="Search item"
                            filterOption={false}
                            onChange={(value) => handleItemSelect(value, name)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item {...restField} label="Item Name" name={[name, 'itemName']}>
                          <Input placeholder="Auto-filled" readOnly />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item
                          {...restField}
                          label="Quantity"
                          name={[name, 'quantity']}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} step={1} style={{ width: '100%' }} onChange={() => calculateItemTotals(name)} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item
                          {...restField}
                          label="Unit Price"
                          name={[name, 'unitPrice']}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} step={0.01} style={{ width: '100%' }} onChange={() => calculateItemTotals(name)} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item {...restField} label="Tax Rate %" name={[name, 'taxRate']}>
                          <Select
                            options={taxOptions}
                            placeholder="Select tax"
                            onChange={() => calculateItemTotals(name)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={6} md={3}>
                        <Form.Item {...restField} label="Total" name={[name, 'totalAmount']}>
                          <InputNumber
                            style={{ width: '100%' }}
                            readOnly
                            formatter={(value) => formatCurrency(Number(value))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item {...restField} name={[name, 'currencyId']} hidden>
                      <InputNumber />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'inventoryItemId']} hidden>
                      <InputNumber />
                    </Form.Item>
                  </Card>
                ))}
                
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 8 }}>
                  Add Purchase Order Item
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {/* Purchase Order Summary Card */}
        <div className="info-card" style={{ marginBottom: 16 }}>
          <div className="info-card-header">
            <h3 className="info-card-title">Purchase Order Summary</h3>
          </div>
          <div className="info-card-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Order Date" name="orderDate" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item label="Delivery Date" name="deliveryDate">
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="Subtotal" name="subTotal">
                  <InputNumber style={{ width: '100%' }} readOnly formatter={(value) => formatCurrency(Number(value))} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Form.Item label="Total Tax" name="totalTax">
                  <InputNumber style={{ width: '100%' }} readOnly formatter={(value) => formatCurrency(Number(value))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Form.Item label={<Text strong>Total Amount</Text>} name="totalAmount">
                  <InputNumber
                    style={{ width: '100%', fontWeight: 'bold', fontSize: '16px' }}
                    readOnly
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>

        {/* Hidden status field */}
        <Form.Item name="status" hidden>
          <Input />
        </Form.Item>

        {/* Save Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" {...saveButtonProps} size="large">
            Create Purchase Order
          </Button>
        </div>
      </Form>
    </div>
  );
};
